/**
 * Media type utility functions for Gallery feature
 */

/**
 * Helper function to detect if a media item is a GIF file
 * Checks both MIME type and file extension for robustness
 *
 * This function is used to:
 * - Filter GIFs from pagination count in content-scene contexts
 * - Filter GIFs from display in content-scene contexts
 * - Validate upload file types (drag-and-drop, file picker)
 *
 * @param media - Media item with type and filename properties
 * @returns true if media is a GIF, false otherwise
 *
 * @example
 * // Standard GIF with correct MIME type
 * isGifMedia({ type: 'image/gif', filename: 'animation.gif' }) // => true
 *
 * @example
 * // Legacy GIF with missing MIME type (relies on extension fallback)
 * isGifMedia({ filename: 'old-animation.gif' }) // => true
 *
 * @example
 * // GIF with wrong MIME type but correct extension
 * isGifMedia({ type: 'image/png', filename: 'misnamed.gif' }) // => true
 *
 * @example
 * // Non-GIF file
 * isGifMedia({ type: 'image/png', filename: 'photo.png' }) // => false
 *
 * @remarks
 * Limitation: Cannot detect GIFs with BOTH wrong MIME type AND wrong extension
 * (e.g., GIF binary with type='image/png' and filename='fake.png')
 * This edge case is extremely rare in practice.
 */
export const isGifMedia = (media: { type?: string; filename?: string; name?: string }): boolean => {
  // Check MIME type first (most reliable)
  if (media.type === "image/gif") {
    return true;
  }

  // Fallback to file extension check for cases where MIME type is missing/incorrect
  const filename = media.filename || media.name || "";
  if (filename) {
    const extension = filename.slice(filename.lastIndexOf(".")).toLowerCase();
    return extension === ".gif";
  }

  return false;
};
